/**
 * advertising.js — Advertising AI prompt tasks.
 * Schema and quality bar: see _exemplar.js. Template language: lib/ai-lab/renderTemplate.js.
 */
export const ADVERTISING_TASKS = [
  // ── 1. Meta ad ──────────────────────────────────────────────────────────────
  {
    id: "advertising-meta-ad",
    category: "advertising",
    title: "Create Meta Ad",
    description: "A complete Meta / Instagram ad set — 5 primary texts, 5 headlines, 3 descriptions, CTA button, launch pairings and a creative direction with image prompt, all inside Meta's character limits.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine from green coffee", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences. No hype words.", brandKey: "brandVoice", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Looking like a sports supplement; excessive neon colors", brandKey: "avoid" },
      { id: "objective", label: "Campaign objective", type: "select", options: ["Awareness", "Traffic", "Conversion", "Retargeting"], default: "Conversion" },
      { id: "offer", label: "Offer (optional)", type: "text", placeholder: "20% off first order with code FIRST20; free shipping over $40" },
      { id: "landingPage", label: "Landing page URL", type: "url", placeholder: "https://", brandKey: "website" },
      { id: "placement", label: "Primary placement", type: "select", options: ["Feed (1:1 / 4:5)", "Stories & Reels (9:16)", "Advantage+ (all placements)"], default: "Advantage+ (all placements)" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
    ],
    template: `Act as a senior paid-social creative strategist who has scaled emerging food & beverage brands on Meta from their first $1,000 in spend to seven figures.

Write a complete Meta (Facebook + Instagram) ad set for {{brandName}}, {{category|article}} targeting {{targetConsumer}}.

PRODUCT
{{productDescription}}{{#if pricePoint}}
Retail price: {{pricePoint}}.{{/if}}

BRAND POSITIONING
{{positioning}}
{{#if claims}}
KEY CLAIMS (use only these — do not invent new claims)
{{claims|list}}{{/if}}
{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. The copy must feel clearly differentiated from how these brands advertise.{{/if}}
{{#if brandVoice}}
BRAND VOICE
{{brandVoice}}{{/if}}

CAMPAIGN OBJECTIVE: {{objective}}{{#if objective=Awareness}}
Write for reach and recall: lead with the brand idea and one memorable product truth, keep the ask light, and make the first line work as a standalone billboard.{{/if}}{{#if objective=Traffic}}
Write for clicks: open a specific curiosity gap or benefit that the landing page pays off, and make the headline the reason to tap.{{/if}}{{#if objective=Conversion}}
Write for purchase: lead with the strongest benefit, stack proof, remove risk (guarantee, shipping, subscribe-and-save) and close with a direct ask.{{/if}}{{#if objective=Retargeting}}
Write for people who already visited or added to cart: acknowledge they know the product, answer the top objection, add social proof and give a truthful reason to buy now. No cold-audience introductions.{{/if}}
{{#if offer}}
OFFER: {{offer}} — feature it in at least 2 primary texts and 2 headlines.{{/if}}
{{#if landingPage}}
LANDING PAGE: {{landingPage}}{{/if}}

PRIMARY PLACEMENT: {{placement}}

CHARACTER LIMITS (hard rules)
- Primary text: the first 125 characters must carry the hook (that is all that shows before "See more"); total under 300 characters
- Headline: 40 characters max (aim for 27)
- Description: 30 characters max
- Show the character count in parentheses after every line

PROVIDE
1. 5 primary texts, each a different angle and labeled: (a) core benefit, (b) problem to solution, (c) social proof / review style, (d) founder or ingredient story, (e) objection-crusher{{#if offer}} or offer-led{{/if}}
2. 5 headlines (mix benefit, outcome, offer and curiosity)
3. 3 descriptions
4. Recommended CTA button from Meta's list (Shop Now, Learn More, Sign Up, Get Offer, Order Now, Subscribe) with one line on why
5. 3 text + headline pairings to launch first, as a table: Pairing | Primary text # | Headline # | Why it should win
6. Creative direction: the single visual that best matches the winning angle — subject, setting, product placement, text overlay (max 6 words) and colors
7. A complete image-generation prompt (for ChatGPT image generation or Midjourney) for that visual in {{#if placement=Stories & Reels (9:16)}}9:16{{/if}}{{#if placement!=Stories & Reels (9:16)}}4:5{{/if}} format
8. 3 hooks for a video version of this ad (first 3 seconds, spoken line + on-screen text)

AVOID{{#if avoid}}
{{avoid|list}}{{/if}}{{#unless avoid}}
- Looking and sounding like every other {{category|lower}} ad{{/unless}}
- Health, disease or weight-loss claims not listed above — flag anything that may need legal or regulatory review instead of rephrasing it
- "Before / after" framing, personal-attribute call-outs ("Are you overweight?") or anything else that breaks Meta advertising policy
- Hype words: revolutionary, game-changing, ultimate, best-ever
- Emojis in headlines; at most 2 in any primary text

Write in plain, confident, specific language a real customer would use. Every line must be pasteable into Ads Manager as-is.`,
    tips: [
      "Paste into ChatGPT or Claude. Then take #7 (the image prompt) into ChatGPT image generation with your product photo uploaded.",
      "Follow up: \"Rewrite primary texts 1 and 3 for a 55+ audience and give me 5 more headlines under 27 characters.\"",
    ],
  },

  // ── 2. Instagram post ───────────────────────────────────────────────────────
  {
    id: "advertising-instagram-post",
    category: "advertising",
    title: "Create Instagram Post",
    description: "One ready-to-publish Instagram post — 3 caption variants, first-line hooks, tiered hashtags, alt text and an image prompt that matches your brand look.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "postTopic", label: "What is this post about?", type: "text", placeholder: "Launching our new Mango flavor / Why we use green coffee / Now at Sprouts", required: true, span: "full" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences. No hype words.", brandKey: "brandVoice", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal, Bold", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "Leave blank to let AI propose", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Looking like a sports supplement; excessive neon colors", brandKey: "avoid" },
      { id: "postFormat", label: "Post format", type: "select", options: ["Single image", "Carousel (3–5 slides)", "Reel cover + caption"], default: "Single image" },
      { id: "captionGoal", label: "Caption goal", type: "select", options: ["Comments & engagement", "Saves & shares", "Link-in-bio clicks", "Product education", "Announce availability / where to buy"], default: "Comments & engagement" },
      { id: "hashtagCount", label: "Hashtags", type: "select", options: ["5", "10", "15"], default: "10" },
    ],
    template: `Act as a senior social media creative lead who has built Instagram accounts for emerging {{category|lower}} and better-for-you CPG brands from zero to six figures of followers.

Create one complete, ready-to-publish Instagram post for {{brandName}}, {{category|article}} for {{targetConsumer}}.

POST TOPIC
{{postTopic}}

PRODUCT
{{productDescription}}

FORMAT: {{postFormat}}
CAPTION GOAL: {{captionGoal}}{{#if captionGoal=Comments & engagement}}
End every caption with a genuine, easy-to-answer question or a "this or that" prompt that invites replies. No "comment below" filler.{{/if}}{{#if captionGoal=Saves & shares}}
Structure the caption as useful, skimmable value (a list, a tip, a myth busted) worth saving or sending to a friend.{{/if}}{{#if captionGoal=Link-in-bio clicks}}
Give one clear reason to tap the link in bio and name exactly what they will find there.{{/if}}{{#if captionGoal=Product education}}
Teach one thing clearly (ingredient, process, benefit) in plain language; no jargon, no lecture.{{/if}}{{#if captionGoal=Announce availability / where to buy}}
Make the where, when and how-to-find unmistakable in the first line and again at the end.{{/if}}
{{#if brandVoice}}
BRAND VOICE
{{brandVoice}}{{/if}}{{#if personality}}
The brand personality is {{personality|join}} — the captions should sound like that person talking, not like a marketing department.{{/if}}

PROVIDE
1. 3 first-line hooks (each under 125 characters so it is fully visible before "more"), labeled by angle
2. 3 complete caption variants using the best hook: (A) short, under 150 characters; (B) medium, 300–500 characters; (C) storytelling, 700–1,000 characters with line breaks. Show the character count for each.{{#if postFormat=Carousel (3–5 slides)}}
3. Slide-by-slide plan: slide number, on-image text (max 10 words), what the image shows, and why someone swipes to the next{{/if}}{{#if postFormat=Reel cover + caption}}
3. Reel cover text (max 5 words) and a 3-second opening frame description{{/if}}{{#if postFormat=Single image}}
3. On-image text recommendation (max 6 words, or "none" if the image is stronger clean){{/if}}
4. {{hashtagCount}} hashtags in three tiers: broad category, niche community, branded / occasion — put them in the first comment or at the caption end, and avoid banned or spammy tags
5. Alt text (under 125 characters) describing the image for accessibility and search
6. A complete image-generation prompt (for ChatGPT image generation or Midjourney, 4:5 aspect ratio) that shows the product in a scene matching the post topic and a visual style that feels {{style|join}}{{#unless style}}premium, modern and distinctive{{/unless}}{{#if brandColors}}, using the brand colors {{brandColors|join}}{{/if}}
7. Best posting window and one line on why, given the target consumer
8. Two Story frames to post the same day that drive people to this post (on-frame text of max 8 words and the sticker to use: poll, question or link)
9. The single first comment to pin (a follow-up detail, the where-to-buy line or the hashtag block)

AVOID{{#if avoid}}
{{avoid|list}}{{/if}}{{#unless avoid}}
- Generic wellness-speak ("fuel your journey") and stock-photo energy{{/unless}}
- Health or disease claims; if a claim needs substantiation, mark it [VERIFY] instead of rewording it
- More than 3 emojis per caption; hashtags inside sentences; engagement bait ("like if you agree")

Write like a confident human, not a brand account. Every variant must be ready to paste.`,
    tips: [
      "Paste into ChatGPT. Then upload your product photo and paste #6 into ChatGPT image generation.",
      "Follow up: \"Turn caption B into a 5-slide carousel and write the slide text.\"",
    ],
  },

  // ── 3. TikTok concepts ──────────────────────────────────────────────────────
  {
    id: "advertising-tiktok-concept",
    category: "advertising",
    title: "Create TikTok Ad Concept",
    description: "Five native-feeling TikTok ad concepts, each with a 2-second hook, timed beats, on-screen text, sound direction and CTA — ranked by which to shoot first.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Looking like a sports supplement; gaming imagery", brandKey: "avoid" },
      { id: "adGoal", label: "Ad goal", type: "select", options: ["Drive DTC website sales", "TikTok Shop conversions", "Awareness & follows", "Retail: find us in store"], default: "Drive DTC website sales" },
      { id: "duration", label: "Target length", type: "select", options: ["9–15 seconds", "15–30 seconds", "30–60 seconds"], default: "15–30 seconds" },
      { id: "creatorType", label: "Who is on camera?", type: "select", options: ["UGC creator", "Founder on camera", "Faceless / product only", "Mix across concepts"], default: "Mix across concepts" },
      { id: "usageOccasions", label: "Usage occasions", type: "tags", placeholder: "Pre-workout, 3pm slump, Road trips" },
    ],
    template: `Act as a senior TikTok creative strategist who has produced top-performing Spark Ads and TikTok Shop creative for emerging food & beverage brands, and who knows that ads that look like ads get skipped in under a second.

Create 5 distinct TikTok ad concepts for {{brandName}}, {{category|article}} for {{targetConsumer}}.

PRODUCT
{{productDescription}}
{{#if positioning}}
POSITIONING
{{positioning}}{{/if}}
{{#if claims}}
CLAIMS YOU MAY USE (nothing beyond these)
{{claims|list}}{{/if}}
{{#if usageOccasions}}
USAGE OCCASIONS TO DRAMATIZE
{{usageOccasions|list}}{{/if}}
{{#if competitors}}
COMPETITIVE SET: {{competitors|join}}. Never name them in the ad; do make the contrast obvious through the concept.{{/if}}
{{#if personality}}
BRAND PERSONALITY: {{personality|join}}{{/if}}

AD GOAL: {{adGoal}}{{#if adGoal=TikTok Shop conversions}}
Every concept must show the product in use within 3 seconds, state price or offer clearly, and end with an on-screen "tap the orange cart" style cue.{{/if}}{{#if adGoal=Retail: find us in store}}
Every concept must end with the retailer name and the exact aisle or section, shown on screen for at least 2 seconds.{{/if}}{{#if adGoal=Awareness & follows}}
Optimize for completion rate and shares; the CTA is a follow or a comment, not a purchase.{{/if}}{{#if adGoal=Drive DTC website sales}}
Optimize for click-through: one clear offer or reason, link-in-bio / shop-now cue in the last 3 seconds.{{/if}}

LENGTH: {{duration}}
ON CAMERA: {{creatorType}}

FORMAT RULES
- Vertical 9:16, designed to be watched with sound on but fully understood with sound off
- The hook must land inside the first 2 seconds and combine a spoken line with on-screen text
- Use a different native TikTok format for each concept (examples: POV, "things that just make sense", taste-test reaction, get-ready-with-me, ASMR pour, 3 reasons, day-in-the-life, green-screen explainer, stitch-style reply, "I was skeptical")
- Keep on-screen text inside the safe zone (avoid the bottom 20% and right edge) and under 8 words per card

FOR EACH CONCEPT PROVIDE
1. Concept name and native format used
2. Hook (0–2 s): exact spoken line + exact on-screen text
3. Beats with timestamps through to the end, one line each: what we see, what is said
4. On-screen text cards in order
5. Sound direction: trending-sound style vs. original voiceover, energy level, and whether dialogue or VO carries the ad
6. CTA (spoken + on-screen)
7. Why it works on TikTok specifically, in one sentence
8. Filming notes: location, props, shots needed, approximate shoot time on a phone

THEN PROVIDE
- A ranking table: Concept | Hook strength (1–10) | Ease to shoot (1–10) | Fit with goal (1–10) | Shoot first?
- 3 alternate hooks for the top-ranked concept

AVOID{{#if avoid}}
{{avoid|list}}{{/if}}{{#unless avoid}}
- Polished TV-commercial aesthetics, stock footage and studio lighting{{/unless}}
- Health or disease claims, "clinically proven" or weight-loss language not listed above; flag anything needing legal review instead of inventing it
- Fake testimonials, invented statistics or results; use [INSERT REAL NUMBER] placeholders where proof is needed
- Cringe brand-voice attempts at slang; keep it natural`,
    tips: [
      "Paste into ChatGPT. Pick the winner and run the Video AI \"Create UGC Script\" task to get a full word-for-word script.",
      "Follow up: \"Write concept 2 as a shot-by-shot list a creator can film in 20 minutes.\"",
    ],
  },

  // ── 4. UGC script ───────────────────────────────────────────────────────────
  {
    id: "advertising-ugc-script",
    category: "advertising",
    title: "Create UGC Script",
    description: "A word-for-word UGC ad script in the hook / problem / solution / proof / CTA structure, timed to 30, 45 or 60 seconds, with alternate hooks, captions and a b-roll shot list.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee extract, L-theanine", brandKey: "ingredients" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Looking like a sports supplement; hype words", brandKey: "avoid" },
      { id: "length", label: "Script length", type: "select", options: ["30 seconds", "45 seconds", "60 seconds"], default: "30 seconds" },
      { id: "creatorPersona", label: "Creator persona", type: "textarea", placeholder: "Busy mom in her 30s who runs before work; skeptical of energy drinks", required: true, span: "full" },
      { id: "usageOccasion", label: "Usage occasion in the script", type: "text", placeholder: "The 3pm slump at a desk job" },
      { id: "platform", label: "Platform", type: "select", options: ["TikTok", "Instagram Reels", "Meta feed ads", "YouTube Shorts"], default: "TikTok" },
      { id: "offer", label: "Offer (optional)", type: "text", placeholder: "20% off with code TRY20" },
    ],
    template: `Act as a senior UGC creative director who writes scripts for the highest-spending DTC food & beverage accounts and briefs creators on how to deliver them naturally.

Write a UGC ad script that runs exactly {{length}} for {{brandName}}, {{category|article}} for {{targetConsumer}}, to run on {{platform}}.

PRODUCT
{{productDescription}}{{#if ingredients}}
Hero ingredients: {{ingredients|join}}.{{/if}}

CREATOR PERSONA (the script is in this person's voice)
{{creatorPersona}}
{{#if usageOccasion}}
SCENE / USAGE OCCASION
{{usageOccasion}}{{/if}}
{{#if claims}}
CLAIMS YOU MAY USE (exactly these — no additions)
{{claims|list}}{{/if}}{{#if competitors}}
The persona may reference "the usual energy options" or "what I used to drink" but must never name {{competitors|join}}.{{/if}}
{{#if brandVoice}}
BRAND VOICE TO KEEP UNDERNEATH THE PERSONA
{{brandVoice}}{{/if}}
{{#if offer}}
OFFER TO CLOSE WITH: {{offer}}{{/if}}

TIMING AND WORD BUDGET{{#if length=30 seconds}}
30 seconds ≈ 70–80 spoken words. Hook 0–3 s, problem 3–8 s, solution 8–18 s, proof 18–25 s, CTA 25–30 s.{{/if}}{{#if length=45 seconds}}
45 seconds ≈ 105–120 spoken words. Hook 0–3 s, problem 3–10 s, solution 10–25 s, proof 25–38 s, CTA 38–45 s.{{/if}}{{#if length=60 seconds}}
60 seconds ≈ 140–160 spoken words. Hook 0–3 s, problem 3–12 s, solution 12–32 s, proof 32–50 s, CTA 50–60 s.{{/if}}

PROVIDE
1. Three alternate hooks (first 3 seconds), each with the spoken line and the on-screen text; mark which one the script uses
2. The full script as a table: Timestamp | Section (Hook / Problem / Solution / Proof / CTA) | Spoken words (verbatim, conversational, contractions allowed) | What the camera sees | On-screen caption
3. Total spoken word count and estimated seconds
4. B-roll shot list: 8–10 specific shots (e.g. "close-up: can cracking open, condensation, natural window light"), with the timestamp each one covers
5. Delivery notes for the creator: energy level, pace, where to pause, where to look at the lens vs. away, what to hold
6. Caption-style guidance for {{platform}}: burned-in captions on, font weight, safe zone, and the 5-word text card to pin on the first frame
7. A 15-second cut-down of the same script (hook + solution + CTA only)

AVOID{{#if avoid}}
{{avoid|list}}{{/if}}{{#unless avoid}}
- Sounding scripted, over-enunciated or like an infomercial{{/unless}}
- Health, disease, weight-loss or "clinically proven" claims not listed above — flag anything that needs legal or regulatory review
- Invented results, invented statistics or fake "I lost / I gained" testimonials; use [CREATOR'S REAL EXPERIENCE] where a personal result belongs
- The words "game-changer", "obsessed", "literally" and "hack"
- Reading claims off the pack; translate each claim into a lived benefit in the persona's words

The script must be usable by a creator on the first read, with no rewriting.`,
    tips: [
      "Paste into ChatGPT. Send the table (#2) and b-roll list (#4) directly to your creator as the brief.",
      "Follow up: \"Write the same script for a 55-year-old male persona and change the usage occasion to a long drive.\"",
      "If you cannot hire a creator yet, take the script into HeyGen or the Video AI \"AI Presenter\" task.",
    ],
  },

  // ── 5. Campaign concept ─────────────────────────────────────────────────────
  {
    id: "advertising-campaign",
    category: "advertising",
    title: "Create Campaign Concept",
    description: "A full campaign platform — big idea, 3 creative territories, channel plan with budget split, messaging matrix by funnel stage, KPI targets and a phased timeline.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "3 min to fill · 2 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice", span: "full" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Looking like a sports supplement; gaming imagery", brandKey: "avoid" },
      { id: "campaignGoal", label: "Campaign goal", type: "select", options: ["Launch the brand or a new product", "Drive trial / first purchase", "Support a retail launch (sell-through)", "Seasonal or occasion push", "Grow brand awareness"], default: "Drive trial / first purchase" },
      { id: "budgetTier", label: "Total media budget", type: "select", options: ["Under $5k", "$5k–$25k", "$25k–$100k", "$100k+"], default: "$5k–$25k" },
      { id: "campaignDuration", label: "Campaign length", type: "select", options: ["2 weeks", "4 weeks", "6–8 weeks", "One quarter"], default: "4 weeks" },
      { id: "seasonalMoment", label: "Seasonal moment / tie-in (optional)", type: "text", placeholder: "Back to school, Dry January, Marathon season" },
      { id: "retailerName", label: "Retailer to support (optional)", type: "text", placeholder: "Sprouts, Whole Foods Southwest region" },
    ],
    template: `Act as a senior integrated campaign strategist from a challenger-brand agency who has launched food & beverage brands against category giants on a fraction of their budget.

Develop a complete campaign concept for {{brandName}}, {{category|article}} targeting {{targetConsumer}}.

PRODUCT
{{productDescription}}

POSITIONING
{{positioning}}
{{#if claims}}
PROOF POINTS AVAILABLE
{{claims|list}}{{/if}}
{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. Identify the category convention they all share and build the campaign to break it.{{/if}}
{{#if personality}}
BRAND PERSONALITY: {{personality|join}}{{/if}}
{{#if brandVoice}}
BRAND VOICE: {{brandVoice}}{{/if}}
{{#if channels}}
WHERE THE PRODUCT IS SOLD: {{channels|join}} — every piece of the campaign must make it obvious where to buy.{{/if}}

CAMPAIGN PARAMETERS
- Goal: {{campaignGoal}}
- Total media budget: {{budgetTier}}
- Duration: {{campaignDuration}}{{#if seasonalMoment}}
- Seasonal moment / tie-in: {{seasonalMoment}}{{/if}}
{{#if retailerName}}- Retail partner to support: {{retailerName}} (include in-store and retailer co-marketing tactics and a "find it at" message){{/if}}{{#if budgetTier=Under $5k}}
Budget reality: this is a founder-run, organic-heavy campaign. Prioritize owned social, creator gifting, email/SMS and a small paid-social test; no production the founder cannot do with a phone.{{/if}}{{#if budgetTier=$5k–$25k}}
Budget reality: paid social plus a handful of paid micro-creators; production must be UGC and AI-assisted, not a film crew.{{/if}}

PROVIDE
1. Consumer insight: the one tension in the target consumer's life this campaign resolves (2–3 sentences)
2. Big idea: one sentence, plus a 60–90 word campaign manifesto in the brand voice
3. Three creative territories, each with: name, the insight it leans on, the idea in one line, tagline, 3 sample headlines, visual world (setting, palette, photography or illustration style), hero format, and the biggest risk
4. Channel plan as a table: Channel | Role in funnel | Formats | % of budget | Primary KPI | Why this channel for this consumer
5. Messaging matrix as a table: rows = audience segments (at least 3), columns = Awareness / Consideration / Conversion / Loyalty, cells = the message and proof point to use
6. KPI targets with realistic benchmark ranges for an emerging {{category|lower}} brand at this budget (e.g. CPM, CTR, CPA, ROAS, email sign-up rate, retail velocity if relevant); state assumptions
7. Phased timeline for {{campaignDuration}}: tease, launch, sustain, close — with what ships in each phase
8. Asset list: every creative asset needed, who makes it (founder / creator / AI tool / designer) and the order to produce them
9. Measurement and optimization plan: what to check on day 3, day 7 and at the halfway point, and the kill / scale rules
10. Recommendation: which territory to run and why, in one paragraph

CONSTRAINTS{{#if avoid}}
{{avoid|list}}{{/if}}{{#unless avoid}}
- No category clichés: no lightning bolts, no sweaty gym montages, no "fuel your" lines{{/unless}}
- Use only the proof points above; flag any claim that would need legal, regulatory or substantiation review and do not invent statistics
- Every territory must be producible at the stated budget
- Tables must be pasteable into a document or Google Sheets`,
    tips: [
      "Paste into Claude (best for long strategy). Then paste the winning territory into the \"Create Meta Ad\" and \"Create TikTok Ad Concept\" tasks.",
      "Follow up: \"Turn the channel plan into a week-by-week Google Sheets table with budget per week.\"",
    ],
  },

  // ── 6. Headlines & hooks ────────────────────────────────────────────────────
  {
    id: "advertising-headlines",
    category: "advertising",
    title: "Create 10 Headlines & 10 Hooks",
    description: "Ten ad headlines and ten video hooks organized by angle — benefit, curiosity, social proof, contrarian, urgency — with character counts, placement fit and A/B test pairings.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "1 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Hype words; sounding like a supplement brand", brandKey: "avoid" },
      { id: "useCase", label: "Where will these run?", type: "select", options: ["Meta / Instagram ads", "TikTok / Reels videos", "Landing page hero", "Email subject lines", "Amazon / retail listing", "All of the above"], default: "Meta / Instagram ads" },
      { id: "offer", label: "Offer (optional)", type: "text", placeholder: "20% off first order" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "socialProof", label: "Real social proof you can use (optional)", type: "textarea", placeholder: "4.8 stars from 312 reviews; sold out twice; stocked in 40 stores", span: "full" },
    ],
    template: `Act as a senior direct-response copywriter who has written winning headlines and video hooks for food & beverage brands and tests dozens of variants a week.

Write 10 headlines and 10 hooks for {{brandName}}, {{category|article}} for {{targetConsumer}}.

PRODUCT
{{productDescription}}{{#if pricePoint}}
Price: {{pricePoint}}.{{/if}}
{{#if positioning}}
POSITIONING
{{positioning}}{{/if}}
{{#if claims}}
CLAIMS YOU MAY USE (exactly these)
{{claims|list}}{{/if}}
{{#if socialProof}}
REAL SOCIAL PROOF YOU MAY USE (exactly these facts, nothing invented)
{{socialProof}}{{/if}}
{{#unless socialProof}}
No verified social proof was supplied: write social-proof lines with [INSERT REAL NUMBER] placeholders and never invent reviews, counts or results.{{/unless}}
{{#if competitors}}
COMPETITIVE SET: {{competitors|join}} — do not name them; do contrast with what they stand for.{{/if}}
{{#if brandVoice}}
BRAND VOICE: {{brandVoice}}{{/if}}
{{#if offer}}
OFFER AVAILABLE FOR URGENCY LINES: {{offer}}{{/if}}

WHERE THESE WILL RUN: {{useCase}}{{#if useCase=Meta / Instagram ads}}
Headlines must be 40 characters or fewer (27 is ideal). Hooks are the first line of primary text or the first 2 seconds of a video.{{/if}}{{#if useCase=TikTok / Reels videos}}
Headlines double as on-screen text cards (max 8 words). Hooks are spoken in the first 2 seconds and must sound like a person, not a brand.{{/if}}{{#if useCase=Landing page hero}}
Headlines can run to 8–10 words with a supporting subhead; hooks become the first sentence of the page.{{/if}}{{#if useCase=Email subject lines}}
Headlines must be 50 characters or fewer and read naturally in an inbox; hooks become preview text (under 90 characters).{{/if}}{{#if useCase=Amazon / retail listing}}
Headlines must be benefit-led, keyword-aware and free of promotional language Amazon rejects; hooks become the first bullet point.{{/if}}{{#if useCase=All of the above}}
Keep headlines under 40 characters so they work everywhere; note the best placement for each.{{/if}}

PROVIDE
1. 10 HEADLINES, 2 per angle, as a table: # | Angle (Benefit / Curiosity / Social proof / Contrarian / Urgency) | Headline | Character count | Best placement
2. 10 HOOKS, 2 per angle, as a table: # | Angle | Hook (max 12 words, written to be spoken or read in 2 seconds) | On-screen text version (max 6 words) | What the next 3 seconds must show
3. Top 3 headlines and top 3 hooks with one sentence each on why they should win
4. 3 A/B test pairings (headline vs. headline) that isolate a single variable (angle, specificity, price mention)
5. 5 bonus "specificity upgrades": take vague lines and add a concrete number, ingredient or moment from the product facts above

RULES
- Every line must be specific to {{brandName}}; if the brand name could be swapped for a competitor's, rewrite it
- Contrarian lines challenge a category belief, never a competitor by name and never a consumer
- Urgency must be truthful (real offer, real stock, real season); no fake countdowns
- No health, disease or weight-loss claims beyond the claims listed; mark anything that needs legal or regulatory review with [REVIEW]{{#if avoid}}
{{avoid|list}}{{/if}}{{#unless avoid}}
- No hype words: revolutionary, ultimate, game-changing, unleash{{/unless}}`,
    tips: [
      "Paste into ChatGPT. Take the top 3 headlines into the \"Create Meta Ad\" task, or the top 3 hooks into the \"Create UGC Script\" task.",
      "Follow up: \"Give me 10 more in the Curiosity angle only, each under 27 characters.\"",
    ],
  },

  // ── 7. Product launch ad set ────────────────────────────────────────────────
  {
    id: "advertising-launch-ad",
    category: "advertising",
    title: "Create Product Launch Ad",
    description: "A full-funnel launch ad set — teaser, launch-day, consideration and retargeting ads with copy, CTAs, creative direction, image prompts, timeline and budget split.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "3 min to fill · 2 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product being launched", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine — new Mango flavor", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "founderStory", label: "Founder story", type: "textarea", placeholder: "2–4 sentences on why you started the brand", brandKey: "founderStory", span: "full" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Looking like a sports supplement; hype words", brandKey: "avoid" },
      { id: "launchType", label: "Launch type", type: "select", options: ["Brand launch (first product)", "New flavor / SKU", "New retailer launch", "Limited edition / seasonal", "Relaunch with new packaging"], default: "Brand launch (first product)" },
      { id: "launchDate", label: "Launch date", type: "text", placeholder: "March 15" },
      { id: "launchOffer", label: "Launch offer (optional)", type: "text", placeholder: "Free shipping + 15% off launch week with code LAUNCH15" },
      { id: "whereToBuy", label: "Where to buy at launch", type: "text", placeholder: "ourbrand.com, Amazon, Sprouts (Southwest)", required: true },
      { id: "primaryChannel", label: "Paid channels", type: "select", options: ["Meta + Instagram", "TikTok", "Meta + TikTok", "Meta + Google / YouTube"], default: "Meta + Instagram" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
    ],
    template: `Act as a senior growth marketer who has run product launches for emerging food & beverage brands and builds launch creative as a full funnel, not a single ad.

Build the complete launch ad set for {{brandName}}, {{category|article}} for {{targetConsumer}}.

LAUNCH
- Type: {{launchType}}{{#if launchDate}}
- Launch date: {{launchDate}}{{/if}}
- Where to buy: {{whereToBuy}}{{#if launchOffer}}
- Launch offer: {{launchOffer}}{{/if}}{{#if pricePoint}}
- Price: {{pricePoint}}{{/if}}
- Paid channels: {{primaryChannel}}

PRODUCT
{{productDescription}}

POSITIONING
{{positioning}}
{{#if claims}}
CLAIMS YOU MAY USE (exactly these)
{{claims|list}}{{/if}}
{{#if founderStory}}
FOUNDER STORY (use for the story-led ads)
{{founderStory}}{{/if}}
{{#if competitors}}
COMPETITIVE SET: {{competitors|join}} — differentiate clearly, never name them.{{/if}}
{{#if brandVoice}}
BRAND VOICE: {{brandVoice}}{{/if}}{{#if launchType=New retailer launch}}
Every ad must name the retailer, show the pack, and include a "find it in the [aisle]" line; add a store-locator or geo-targeting note.{{/if}}{{#if launchType=Limited edition / seasonal}}
Build truthful scarcity into every phase (real dates, real quantities as [INSERT]); never fake a countdown.{{/if}}{{#if launchType=Relaunch with new packaging}}
Address existing customers directly ("same formula, new look") and show old-vs-new only if it flatters the new design.{{/if}}

WRITE ONE AD PER ROW IN THIS FUNNEL (9 ads total), each with: Ad name | Audience | Primary text (hook in the first 125 characters, under 300 total) | Headline (under 40 characters) | Description (under 30 characters) | CTA button | Creative direction (format, visual, on-image text of max 6 words) | Character counts
1. TEASE (7–10 days before launch, 2 ads): curiosity and waitlist / notify-me; no product reveal in the first
2. LAUNCH DAY (3 ads, cold audiences): (a) hero benefit, (b) founder story, (c) proof or ingredient story
3. CONSIDERATION (2 ads, engagers and video viewers): objection handling and "how to use it / when to drink it"
4. CONVERSION / RETARGETING (2 ads, site visitors and cart abandoners): offer-led and social-proof-led, with a truthful reason to act this week

THEN PROVIDE
5. Two complete image-generation prompts (ChatGPT image generation, 4:5) for the hero launch visual and the founder visual, consistent in lighting and palette
6. Three 3-second video hooks for the launch-day ads (spoken + on-screen text)
7. Launch timeline as a table: Day | Phase | Ads live | Organic / email action | What to watch
8. Budget split across phases and audiences in percentages, with the scale / kill rule for each phase
9. Landing-page checklist: the 6 things the page must say or show to match the ads (message match, price, where to buy, proof, FAQ, offer terms)

AVOID{{#if avoid}}
{{avoid|list}}{{/if}}{{#unless avoid}}
- Generic launch language: "finally here", "introducing", "the wait is over"{{/unless}}
- Health, disease or weight-loss claims beyond the list; flag anything needing legal or regulatory review with [REVIEW]
- Invented reviews, press quotes or numbers; use [INSERT REAL] placeholders
- Anything that violates Meta or TikTok ad policy (personal attributes, before / after, misleading scarcity)`,
    tips: [
      "Paste into ChatGPT or Claude. Then run the Video AI \"Create Product Launch Video\" task for the hero video that goes with it.",
      "Follow up: \"Rewrite the 3 launch-day ads for a 55+ audience and add 2 Story-format versions.\"",
    ],
  },

  // ── 8. Analyze existing ad ──────────────────────────────────────────────────
  {
    id: "advertising-analyze-ad",
    category: "advertising",
    title: "Analyze Existing Ad",
    description: "A scored teardown of an ad you already have — 1–10 on nine criteria, the three fixes that matter most, a full rewrite, two alternate angles and compliance flags.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "2 min to fill · 1 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "pasteContent", label: "Paste your ad", type: "textarea", placeholder: "Primary text:\nHeadline:\nDescription:\nCTA:\nVisual (describe it or paste the video script):", required: true, span: "full", help: "Paste the copy exactly as it runs and describe the image or video in a few lines." },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice", span: "full" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Hype words; sounding like a supplement brand", brandKey: "avoid" },
      { id: "adPlatform", label: "Platform", type: "select", options: ["Meta / Instagram", "TikTok", "Google / YouTube", "Amazon Sponsored", "Other"], default: "Meta / Instagram" },
      { id: "adObjective", label: "Objective", type: "select", options: ["Awareness", "Traffic", "Conversion", "Retargeting"], default: "Conversion" },
      { id: "performanceNotes", label: "Performance so far (optional)", type: "textarea", placeholder: "CTR 0.8%, CPA $48, 3-second view rate 18%, ran 10 days at $50/day", span: "full" },
    ],
    template: `Act as a senior paid-media creative director who audits ad accounts for emerging food & beverage brands and has reviewed thousands of {{adPlatform}} ads against their actual results.

Analyze this {{adPlatform}} ad for {{brandName}}, {{category|article}} targeting {{targetConsumer}}. Objective: {{adObjective}}.

THE AD AS IT RUNS
{{pasteContent}}
{{#if performanceNotes}}
PERFORMANCE SO FAR
{{performanceNotes}}
Use these numbers to diagnose where the funnel is leaking (hook, click, landing, offer) before judging the copy.{{/if}}
{{#if positioning}}
WHAT THE BRAND STANDS FOR
{{positioning}}{{/if}}
{{#if claims}}
APPROVED CLAIMS (anything else in the ad is a compliance flag)
{{claims|list}}{{/if}}
{{#if brandVoice}}
BRAND VOICE IT SHOULD SOUND LIKE
{{brandVoice}}{{/if}}
{{#if competitors}}
COMPETITIVE SET: {{competitors|join}} — judge whether this ad could be mistaken for one of theirs.{{/if}}

PROVIDE
1. First impression: what a scrolling {{targetConsumer}} understands in the first 1.5 seconds, in one sentence
2. Scorecard as a table, 1–10 with a one-line reason per row:
   - Stop power / hook
   - Clarity of the value proposition
   - Audience fit and relevance
   - Brand voice and distinctiveness
   - Proof and credibility
   - Offer and CTA strength
   - Visual / copy match and creative direction
   - Platform-native feel for {{adPlatform}}
   - Compliance and claim risk (10 = no risk)
   Then an overall score (average) and a verdict: Scale / Fix / Kill
3. The 3 fixes that would move results most, ranked by expected impact, each with the specific change
4. Full rewrite that keeps the original idea but fixes the problems: primary text (hook in first 125 characters), headline (under 40 characters), description (under 30 characters), CTA, and a revised creative direction with on-image text — show character counts
5. Two alternate angles that test a different hypothesis (e.g. problem-led vs. proof-led), each with primary text and headline
6. A visual revision note: what to change in the image or first 3 seconds of video and why
7. Compliance flags: list every claim, word or visual that may breach {{adPlatform}} policy or needs legal / regulatory review; quote it and suggest compliant wording — do not invent substantiation
8. Test plan: which version to run against the original, the single variable isolated, the minimum spend and days before judging

RULES
- Be direct and specific; quote the exact words you are criticizing
- Do not pad the score to be polite; a 4 is a 4
- Stay within the approved claims; flag, do not fabricate{{#if avoid}}
- The rewrite must also avoid: {{avoid}}{{/if}}{{#unless avoid}}
- The rewrite must avoid hype words and generic category clichés{{/unless}}`,
    tips: [
      "Paste into Claude. If you have a screenshot of the ad, upload it in the same chat so the visual gets scored too.",
      "Follow up: \"Now rewrite it as a 30-second UGC script using fix #1.\"",
    ],
  },

  // ── 9. Static ad visual (image) ─────────────────────────────────────────────
  {
    id: "advertising-static-ad-image",
    category: "advertising",
    title: "Create Static Ad Visual",
    description: "A ready-to-paste image-generation prompt for a paid-social static ad with your headline on it, in 1:1, 4:5 or 9:16.",
    outputType: "image",
    recommendedTools: ["chatgpt-images", "ideogram", "midjourney", "canva"],
    estimatedTime: "1 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek matte black can with orange wordmark", required: true, brandKey: "productDescription", span: "full" },
      { id: "headline", label: "Headline on the ad", type: "text", placeholder: "Clean energy. Zero sugar.", required: true, span: "full" },
      { id: "supportingText", label: "Supporting line (optional)", type: "text", placeholder: "200 mg natural caffeine from green coffee" },
      { id: "ctaText", label: "Button text (optional)", type: "text", placeholder: "Shop now" },
      { id: "format", label: "Format", type: "select", options: ["1:1 square (feed)", "4:5 portrait (feed)", "9:16 vertical (Stories / Reels)"], default: "4:5 portrait (feed)" },
      { id: "sceneType", label: "Scene", type: "select", options: ["Clean studio on brand color", "Lifestyle moment in use", "Ingredient splash / hero", "Flat lay with props", "Bold typographic poster"], default: "Clean studio on brand color" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal, Bold", brandKey: "visualStyle" },
      { id: "brandColors", label: "Brand colors", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "fonts", label: "Brand fonts (optional)", type: "tags", placeholder: "Inter, Playfair Display", brandKey: "fonts" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon glow; gaming aesthetics; stock-photo models", brandKey: "avoid" },
    ],
    template: `Act as a senior CPG advertising art director and generate one finished paid-social static ad image for {{brandName}}, {{category|article}}.

REFERENCE
Use the uploaded product image as the exact packaging and the uploaded logo if provided; do not redesign the pack.

SUBJECT
{{productDescription}}
hero product as the clear focal point, label facing camera, occupying roughly 40–50% of the frame
brand name "{{brandName}}" crisp and legible on pack, no warped or invented lettering, single product only

SCENE{{#if sceneType=Clean studio on brand color}}
seamless studio backdrop in one flat brand color, product standing on a subtle matte surface with a soft contact shadow, generous negative space reserved for the headline, nothing else in frame{{/if}}{{#if sceneType=Lifestyle moment in use}}
real-feeling everyday moment where the product is being reached for or held, natural environment matching the {{category|lower}} usage occasion, shallow depth of field so the setting reads but the product stays sharp, authentic not staged{{/if}}{{#if sceneType=Ingredient splash / hero}}
product suspended mid-air with its key ingredients or flavor elements bursting around it, frozen-motion liquid or particle splash, dramatic but clean, elements never covering the label or the headline zone{{/if}}{{#if sceneType=Flat lay with props}}
top-down flat lay on a textured surface, product centered with 4–6 carefully chosen props that explain the usage occasion, even spacing, editorial magazine composition, headline zone kept clear{{/if}}{{#if sceneType=Bold typographic poster}}
poster-style layout where the headline is as big as the product, oversized type interacting with the pack, strong grid, flat color fields, Swiss-poster confidence, still a photoreal product{{/if}}

TEXT OVERLAY (render exactly, spelled exactly)
headline: "{{headline}}"{{#if supportingText}}
supporting line: "{{supportingText}}" in a smaller weight directly beneath the headline{{/if}}{{#if ctaText}}
button: a small rounded pill reading "{{ctaText}}" in the lower third{{/if}}
typography: large, high-contrast headline in a typeface {{#if fonts}}matching {{fonts|join}}{{/if}}{{#unless fonts}}with a clean modern grotesque feel{{/unless}}, strong hierarchy, left-aligned or centered
placement: text never overlaps the product, all text inside a safe margin of at least 8% from every edge

LIGHTING AND CAMERA
soft directional key light with a gentle rim highlight on the pack, subtle realistic reflections
true-to-life color, 85mm product-photography look, tack-sharp label, no lens flare

STYLE AND COLOR
overall feel {{style|join}}{{#unless style}}premium, modern, distinctive{{/unless}}, high-end DTC advertising quality{{#if brandColors}}
palette built strictly from the brand colors {{brandColors|join}}: one as the dominant background, one as the accent{{/if}}{{#unless brandColors}}
restrained two-color palette pulled from the packaging itself{{/unless}}
scroll-stopping contrast between product and background

FORMAT{{#if format=1:1 square (feed)}}
1:1 square, 1080 x 1080 pixels, composition balanced for a square feed placement{{/if}}{{#if format=4:5 portrait (feed)}}
4:5 portrait, 1080 x 1350 pixels, product in the lower two thirds and headline in the top third{{/if}}{{#if format=9:16 vertical (Stories / Reels)}}
9:16 vertical, 1080 x 1920 pixels, headline and button kept out of the top 14% and bottom 20% where the platform UI sits{{/if}}
single image, no borders, no mockup frame, no phone or device around it

NEGATIVE PROMPT
no extra words, no misspellings, no gibberish text, no watermark, no logos other than the brand's own
no additional products, no hands with extra fingers, no stock-photo smiles, no neon glow, no lightning bolts, no clutter, no blurry label{{#if avoid}}
strictly avoid: {{avoid}}{{/if}}

TOOL NOTES
- ChatGPT image generation or Ideogram: paste as-is with the product image uploaded; both render the headline reliably
- Midjourney: remove the TEXT OVERLAY section, add --ar {{#if format=1:1 square (feed)}}1:1{{/if}}{{#if format=4:5 portrait (feed)}}4:5{{/if}}{{#if format=9:16 vertical (Stories / Reels)}}9:16{{/if}} --style raw, then set the headline in Canva`,
    tips: [
      "Upload your product image (and logo) first, then paste this prompt. ChatGPT image generation or Ideogram are best because the headline has to be spelled correctly.",
      "Follow up: \"Make 3 variations: swap the background to each of my brand colors and keep the text identical.\"",
      "Finish in Canva: drop the image in, re-type the headline in your real font and export all three sizes.",
    ],
  },
];
